import React, { Component } from 'react';
import { AnimatedButton } from '@trlogic/ui-pack';
import { TransactionJsonModel, TransactionVariableJsonModel } from '../../domain/stream/transaction/TransactionDomains';
import FileUploader, { UploadProgress } from './FileUploader';
import { apiUrl, InstanceContainer } from '../../config/binding/InstanceBinding';
import Injectables from '../../constant/Injectables';
import EnvironmentUtil from '../../util/EnvironmentUtil';
import RoleHelper from '../../RoleHelper';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from '../custom/FormicaSearchSelectBox';
import HttpMethod from '../../constant/HttpMethod';
import HttpClient from '../../gateway/HttpClient';
import GatewayContainer from '../../config/GatewayContainer';
import GatewayNames from '../../constant/GatewayNames';
import LanguageUtil from '../../util/LanguageUtil';

class SandboxCsvContainer extends Component<Props, State> {
  public state = new State();

  private httpClient: HttpClient = GatewayContainer.getInstance().get(GatewayNames.API_CLIENT);
  private readonly environmentUtil: EnvironmentUtil = InstanceContainer.get(Injectables.ENVIRONMENT_UTIL);

  public render() {
    const { csvFormat, percent, file, progress, selectedTransactionVariable, selectedTransaction } = this.state;

    return (
      <div className={'flex flex-col w-full h-full py-5 px-5 gap-y-2'}>
        <div className={'flex items-center justify-between gap-x-5 gap-y-5'}>
          <div className={'flex gap-x-5 items-center w-3/4 '}>
            <div className={'flex items-center w-full'}>
              <FormicaSearchSelectBox
                placeholder={LanguageUtil.get({ id: 'common.label.transaction', defaultMessage: 'Channel' })}
                disabled={!RoleHelper.canEdit('SandboxCsv')}
                options={this.prepareTransactionSelectBox()}
                value={selectedTransaction?.name}
                onChange={(e: FormicaSelectBoxItem) => this.onChangeSelectedTransaction(e.value)}
              />
            </div>
            <div className={'flex items-center w-full'}>
              <FormicaSearchSelectBox
                placeholder={LanguageUtil.get({ id: 'common.input.placeholder.key', defaultMessage: 'Key' })}
                disabled={!RoleHelper.canEdit('SandboxCsv')}
                options={this.prepareTransactionVariables()}
                value={selectedTransactionVariable?.name}
                onChange={(e: FormicaSelectBoxItem) => this.onChangeTransactionVariable(e.value)}
              />
            </div>
          </div>
          <div className={'flex gap-x-5 items-center'}>
            <div className={'flex px-1 py-1 max-h-[34px] rounded gap-x-1 w-[200px] bg-[#F7F8FA]'}>
              <div
                onClick={() => this.onChangeCsvFormat('semi colon')}
                className={`flex ${csvFormat == 'semi colon' ? 'bg-white opacity-100 ' : 'opacity-50'} items-center justify-center w-1/2 px-1 py-1.5 text-xs text-primary rounded  cursor-pointer`}
              >
                {LanguageUtil.get({ id: 'pages.sandbox.semiColon', defaultMessage: 'Semi Colon' })}
              </div>
              <div
                onClick={() => this.onChangeCsvFormat('comma')}
                className={`flex ${csvFormat == 'comma' ? 'bg-white opacity-100 ' : 'opacity-50'} items-center justify-center w-1/2 px-1 py-1.5 text-xs text-primary rounded  cursor-pointer`}
              >
                {LanguageUtil.get({ id: 'pages.sandbox.comma', defaultMessage: 'Comma' })}
              </div>
            </div>
            <AnimatedButton
              disabled={!RoleHelper.canEdit('SandboxCsv') || selectedTransaction == undefined || selectedTransactionVariable == undefined || file == undefined}
              icon={'deploy'}
              customType={'primary'}
              text={LanguageUtil.get({ id: 'common.button.send', defaultMessage: 'Send' })}
              onClick={this.uploadFile}
            />
          </div>
        </div>
        <div className={'flex flex-col gap-y-2 items-center justify-center w-full h-full border-2 border-dashed'}>
          <FileUploader
            disabled={!RoleHelper.canEdit('SandboxCsv') && selectedTransaction != undefined}
            acceptedFiles={['text/csv']}
            file={file}
            percent={percent}
            progress={progress}
            onChangeFile={this.onChangeFile}
            maxBytes={this.environmentUtil.getStorageMaxSize()}
          />
        </div>
      </div>
    );
  }

  private prepareTransactionSelectBox = () => {
    const { transactions } = this.props;
    return transactions.map((transaction: TransactionJsonModel) => {
      return {
        label: transaction.name,
        value: transaction.name,
      };
    });
  };

  private prepareTransactionVariables = () => {
    const { selectedTransaction } = this.state;
    if (selectedTransaction) {
      return selectedTransaction.variables.map((variable: any) => {
        return {
          label: variable.name,
          value: variable.name,
        };
      });
    }
    return [];
  };

  private onChangeSelectedTransaction = (name: string) => {
    const { transactions } = this.props;
    const selectedTransaction = transactions.find((transaction: TransactionJsonModel) => transaction.name === name);
    if (selectedTransaction) {
      this.setState({ selectedTransaction, transactionVariables: selectedTransaction.variables });
    }
  };

  private onChangeTransactionVariable = (name: string) => {
    const { transactionVariables } = this.state;
    if (transactionVariables) {
      const selectedTransactionVariable = transactionVariables.find((variable: TransactionVariableJsonModel) => variable.name === name);
      this.setState({ selectedTransactionVariable });
    }
  };

  private onChangeCsvFormat = (value: 'comma' | 'semi colon') => {
    if (!RoleHelper.canEdit('SandboxCsv')) return;
    this.setState({ csvFormat: value });
  };

  private uploadFile = async () => {
    const { file, csvFormat, selectedTransaction, selectedTransactionVariable } = this.state;
    const endpoint = `${apiUrl}/file/csv`;
    if (selectedTransaction == undefined || selectedTransactionVariable == undefined || file == undefined) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('transaction', selectedTransaction.name);
    formData.append('semicolon', csvFormat == 'semi colon' ? 'true' : 'false');
    formData.append('key', selectedTransactionVariable.name);
    try {
      await this.httpClient.execute(HttpMethod.POST, endpoint, formData, undefined, undefined, (progressEvent: ProgressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        this.setState({ percent: percentCompleted, progress: 'uploading' });
      });
    } catch (e) {
      console.error(e);
    }
  };

  private onChangeFile = (file: File | null) => {
    this.setState({ file });
  };
}

class State {
  selectedTransaction: TransactionJsonModel | undefined = undefined;
  selectedTransactionVariable: TransactionVariableJsonModel | undefined = undefined;
  transactionVariables: TransactionVariableJsonModel[] = [];
  csvFormat: 'comma' | 'semi colon' = 'semi colon';
  percent: number = 0;
  progress: UploadProgress = 'idle';
  file: File | null = null;
}

interface Props {
  transactions: TransactionJsonModel[];
}

export default SandboxCsvContainer;
